import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import type {
  DocumentSearchResult,
  Highlight,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { useMemo } from 'react'
import { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'

export type ViewMode = 'card' | 'table'

export interface UseActiveDocumentPreviewParams {
  documentResults: DocumentTableRow[]
  activeIndex: number
  selectedDocumentId: string | null
  selectedTableDocument: DocumentTableRow | null
  isLoadingSelectedDocument: boolean
  elasticsearchHighlightsMap: Map<string, Highlight[]>
  rawSearchData: DocumentSearchResult | undefined
  contentFilter: DocumentsViewerContentFilter
  viewMode: ViewMode
}

export interface UseActiveDocumentPreviewResult {
  activeDocument: DocumentTableRow | null
  activeDocumentHighlights: Highlight[] | null
  relevanceScore: number | undefined
  detailScores: Record<string, number> | undefined
  isPreviewOpen: boolean
  isLoadingPreview: boolean
}

function useScoreMaps(rawSearchData: DocumentSearchResult | undefined) {
  return useMemo(() => {
    const scoresMap = new Map<string, number>()
    const detailScores = new Map<string, Record<string, number>>()

    for (const r of rawSearchData?.results ?? []) {
      scoresMap.set(r.document_id, r.score)
      const details: Record<string, number> = {}
      if (r.elasticsearch_score !== null && r.elasticsearch_score !== undefined)
        details.elasticsearch = r.elasticsearch_score
      if (r.pinecone_score !== null && r.pinecone_score !== undefined)
        details.pinecone = r.pinecone_score
      if (Object.keys(details).length > 0) {
        detailScores.set(r.document_id, details)
      }
    }
    return { scoresMap, detailScores }
  }, [rawSearchData])
}

/**
 * Shared hook for active document preview state in both card and table views.
 * Returns the active document (from card list index or table selection), its highlights,
 * relevance scores, and loading state so a single DocumentPreviewPanel can be used for both.
 */
export function useActiveDocumentPreview({
  documentResults,
  activeIndex,
  selectedDocumentId,
  selectedTableDocument,
  isLoadingSelectedDocument,
  elasticsearchHighlightsMap,
  rawSearchData,
  contentFilter,
  viewMode,
}: UseActiveDocumentPreviewParams): UseActiveDocumentPreviewResult {
  const { scoresMap, detailScores } = useScoreMaps(rawSearchData)

  return useMemo(() => {
    if (contentFilter !== 'documents') {
      return {
        activeDocument: null,
        activeDocumentHighlights: null,
        relevanceScore: undefined,
        detailScores: undefined,
        isPreviewOpen: false,
        isLoadingPreview: false,
      }
    }

    if (viewMode === 'card') {
      const activeDocument = documentResults[activeIndex] ?? null
      const activeDocumentHighlights = activeDocument
        ? (elasticsearchHighlightsMap.get(activeDocument.id) ?? null)
        : null
      return {
        activeDocument,
        activeDocumentHighlights,
        relevanceScore: activeDocument
          ? scoresMap.get(activeDocument.id)
          : undefined,
        detailScores: activeDocument
          ? detailScores.get(activeDocument.id)
          : undefined,
        isPreviewOpen: !!activeDocument,
        isLoadingPreview: false,
      }
    }

    // Table view
    const activeDocument = selectedTableDocument
    const activeDocumentHighlights =
      activeDocument && selectedDocumentId
        ? (elasticsearchHighlightsMap.get(selectedDocumentId) ?? null)
        : null
    return {
      activeDocument,
      activeDocumentHighlights,
      relevanceScore: activeDocument
        ? scoresMap.get(activeDocument.id)
        : undefined,
      detailScores: activeDocument
        ? detailScores.get(activeDocument.id)
        : undefined,
      isPreviewOpen: !!selectedDocumentId,
      isLoadingPreview: !!selectedDocumentId && isLoadingSelectedDocument,
    }
  }, [
    contentFilter,
    viewMode,
    documentResults,
    activeIndex,
    selectedDocumentId,
    selectedTableDocument,
    isLoadingSelectedDocument,
    elasticsearchHighlightsMap,
    scoresMap,
    detailScores,
  ])
}
